import { useMemo } from "react";

/**
 * Hook to merge lease config values with a predefined config template.
 * - Filters only matching keys
 * - Updates default values from input
 * - Returns original template if input not provided
 */
export const useTransFormLeaseConfigData = (configValues?: Record<string, any>, template: any[] = []) => {
  const getNestedValue = (obj: any, path: string) => {
    return path.split(".").reduce((acc, key) => acc?.[key], obj);
  };

  const updatedConfig = useMemo(() => {
    // ✅ If configValues is missing or empty → return full template
    if (!configValues || Object.keys(configValues).length === 0) {
      return template;
    }

    // ✅ Filter + update template items that exist in configValues
    const filtered = template
      .filter((field) => {
        const value = getNestedValue(configValues, field.name);
        return value !== undefined;
      })
      .map((field) => {
        const newField = { ...field };
        const value = getNestedValue(configValues, field.name);
        if (value !== undefined) {
          field?.type?.toLowerCase() === "select"
            ? (newField.default = { label: value, value: value })
            : (newField.default = value);
        }

        // Handle checkbox options if present
        if (newField.type === "checkbox" && Array.isArray(newField.options)) {
          newField.options = newField.options.map((opt) => {
            const optValue = getNestedValue(configValues, opt.name);

            if (optValue !== undefined) {
              return { ...opt, default: optValue };
            }
            return opt;
          });
        }

        return newField;
      });

    // ✅ Sort: text first, then switches/checkboxes (optional)
    const sorted = filtered.sort((a, b) => {
      const aIsSpecial = ["switch", "checkbox"].includes(a.type);
      const bIsSpecial = ["switch", "checkbox"].includes(b.type);
      return aIsSpecial === bIsSpecial ? 0 : aIsSpecial ? 1 : -1;
    });
    return sorted;
  }, [configValues, template]);

  return updatedConfig;
};
